import React, { forwardRef } from 'react'
import cx from 'classnames'

import s from './Box.scss'


const allowedKeys = [ 'm', 'mv', 'mh', 'mt', 'mr', 'mb', 'ml', 'p', 'pv', 'ph', 'pt', 'pr', 'pb', 'pl' ] as const
const allowedSizes = [ 4, 8, 12, 16, 20, 24, 32, 36, 40, 48, 56, 60, 64, 72, 80, 96 ] as const

type Key = typeof allowedKeys[number]
export type BoxSize = typeof allowedSizes[number]
export type BoxSizeProps = { [ key in Key ]?: BoxSize }

const getClasses = (keys: BoxSizeProps): string[] => {
  const classes = []
  const newKeys = Object.keys(keys).reduce((newKeys, key) => {
    const value = keys[key]

    if (key === 'mv') {
      newKeys.mt = value
      newKeys.mb = value
    }
    else if (key === 'mh') {
      newKeys.ml = value
      newKeys.mr = value
    }
    else if (key === 'pv') {
      newKeys.pt = value
      newKeys.pb = value
    }
    else if (key === 'ph') {
      newKeys.pl = value
      newKeys.pr = value
    }
    else {
      newKeys[key] = value
    }

    return newKeys
  }, {} as BoxSizeProps)

  Object.keys(newKeys).forEach((key: Key) => {
    let value = newKeys[key]

    if (!value) {
      return
    }

    classes.push(s[`${key}_${value}`])
  })

  return classes
}

export type BoxProps = {
  children?: React.ReactNode
  id?: string
  className?: string
  alignCenter?: boolean
  alignRight?: boolean
  alignLeft?: boolean
  center?: boolean
  inline?: boolean
  relative?: boolean
  lineHeight0?: boolean
  m?: BoxSize
  mv?: BoxSize
  mh?: BoxSize
  mt?: BoxSize
  mr?: BoxSize
  mb?: BoxSize
  ml?: BoxSize
  p?: BoxSize
  pv?: BoxSize
  ph?: BoxSize
  pt?: BoxSize
  pr?: BoxSize
  pb?: BoxSize
  pl?: BoxSize
  tag?: string
  noWrapper?: boolean
  hideEmpty?: boolean
  role?: string
  ariaLabel?: string
  ariaLabelledBy?: string
  dataTestId?: string
}

const Box = forwardRef<HTMLElement, BoxProps>((props, ref) => {
  const {
    children, id, className,
    alignCenter, alignRight, alignLeft,
    center, inline, relative, lineHeight0,
    tag = 'div', noWrapper, hideEmpty, role, ariaLabelledBy, ariaLabel, dataTestId,
    ...keys
  } = props

  const rootClassName = cx(className, ...getClasses(keys), {
    [s.center]: center,
    [s.inline]: inline,
    [s.relative]: relative,
    [s.alignCenter]: alignCenter,
    [s.alignRight]: alignRight,
    [s.alignLeft]: alignLeft,
    [s.lineHeight0]: lineHeight0,
    [s.hideEmpty]: hideEmpty,
  })

  if (noWrapper && React.isValidElement(children)) {
    return React.cloneElement(children, {
      className: cx(children.props.className, rootClassName),
    })
  }

  return React.createElement(tag, {
    ref,
    id,
    className: rootClassName,
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'data-testid': dataTestId,
  }, children)
})

Box.displayName = 'Box'


export default Box
